import React, { useEffect, useState } from "react";
import { Download, X, Share, MonitorDown } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";

/**
 * Üst bardaki "uygulamayı cihazına kur" düğmesi — dokununca DOĞRUDAN kurulum.
 *
 * Chrome/Edge (Android + masaüstü): kurulum izni (beforeinstallprompt) sayfa yüklenirken
 * TEK SEFER gelir ve src/index.js'te en başta yakalanıp window.__deferredInstallPrompt'a
 * konur — düğme ona basınca tarayıcının KENDİ kurulum penceresi açılır, yönerge yok.
 * Kullanıcı kurulumu reddederse hiçbir şey açılmaz (ısrarcı pencere yok).
 *
 * iOS Safari programatik kuruluma İZİN VERMEZ (Apple API sunmuyor) — yalnız orada,
 * tek mümkün yol olan "Paylaş → Ana Ekrana Ekle" yönergesi gösterilir.
 *
 * Uygulama zaten kuruluysa (standalone) düğme hiç görünmez.
 */

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

declare global {
  interface Window {
    __deferredInstallPrompt?: InstallPromptEvent | null;
  }
}

const isStandaloneMode = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)")?.matches ||
    // iOS Safari'nin kendi bayrağı
    (window.navigator as unknown as { standalone?: boolean })?.standalone === true);

const isIos = () => /iphone|ipad|ipod/i.test(typeof navigator === "undefined" ? "" : navigator.userAgent);

const InstallAppButton: React.FC = () => {
  const { currentTheme } = useTheme();
  const [isInstalled, setIsInstalled] = useState(isStandaloneMode);
  const [showIosGuide, setShowIosGuide] = useState(false);
  // Görünürlük: kurulum izni hazır OLDUĞUNDA ya da iOS'ta (yönerge tek yol) göster.
  const [canPrompt, setCanPrompt] = useState(() => Boolean(window.__deferredInstallPrompt));

  useEffect(() => {
    const handleReady = () => setCanPrompt(true);
    const handleInstalled = () => {
      setIsInstalled(true);
      setCanPrompt(false);
      setShowIosGuide(false);
    };
    window.addEventListener("installPromptReady", handleReady);
    window.addEventListener("appinstalled", handleInstalled);
    return () => {
      window.removeEventListener("installPromptReady", handleReady);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  if (isInstalled) return null;
  // Tarayıcı kurulum izni vermemişse ve iOS da değilse düğme gösterilmez —
  // basınca hiçbir şey yapamayacak bir düğmeden iyidir.
  if (!canPrompt && !isIos()) return null;

  const handleClick = async () => {
    const deferred = window.__deferredInstallPrompt;
    if (deferred) {
      // Doğrudan tarayıcının kurulum penceresi. Reddederse ısrar edilmez;
      // tarayıcı aynı olayı tekrar verdiğinde düğme yeniden aktifleşir.
      window.__deferredInstallPrompt = null;
      setCanPrompt(false);
      await deferred.prompt();
      await deferred.userChoice.catch(() => undefined);
      return;
    }
    if (isIos()) setShowIosGuide(true);
  };

  const iosSteps = [
    { icon: Share, text: "Safari'nin altındaki Paylaş simgesine dokun." },
    { icon: Download, text: '"Ana Ekrana Ekle" seçeneğini seç.' },
    { icon: MonitorDown, text: '"Ekle" deyince uygulama simgesi ana ekranında görünür.' },
  ];

  return (
    <>
      <button
        onClick={handleClick}
        className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white rounded transition-all duration-200 touch-manipulation"
        title="Uygulamayı cihazına kur"
        aria-label="Uygulamayı cihazına kur"
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
        }}>
        <Download size={16} />
      </button>

      {showIosGuide && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowIosGuide(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: `linear-gradient(to bottom right, ${currentTheme.colors.background.base}f7, ${currentTheme.colors.background.via}f7)`,
              border: `1px solid ${currentTheme.colors.primary}4d`,
            }}>
            <div
              className="flex items-center justify-between px-5 py-4 border-b"
              style={{ borderColor: `${currentTheme.colors.primary}33` }}>
              <div className="flex items-center gap-2 text-white font-semibold">
                <Download size={18} style={{ color: currentTheme.colors.primary }} />
                Uygulamayı iPhone'una kur
              </div>
              <button
                onClick={() => setShowIosGuide(false)}
                className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white rounded-lg transition-colors"
                aria-label="Kapat">
                <X size={18} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-white/80">
                iPhone'da kurulum yalnız Safari üzerinden yapılabiliyor; iki dokunuş yeter:
              </p>
              {iosSteps.map((step, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${currentTheme.colors.primary}33` }}>
                    <step.icon size={16} style={{ color: currentTheme.colors.primary }} />
                  </div>
                  <div className="text-sm text-white/90 pt-1.5">{step.text}</div>
                </div>
              ))}
            </div>

            <div className="px-5 pb-5">
              <button
                onClick={() => setShowIosGuide(false)}
                className="w-full py-2.5 rounded-xl text-white text-sm font-semibold transition-opacity hover:opacity-90"
                style={{
                  background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})`,
                }}>
                Anladım
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppButton;
